import { observable, computed, action } from 'mobx';
import Alert from 'react-s-alert';

import { Model, App, ModelRegistry, AppRegistry, Input } from 'core/index';
import { analytics } from 'core/analytics';

import { conf as globalConf } from 'apps/config.yaml';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import SignInModal from 'apps/signIn/main/modal';
import { raiseUserError } from 'apps/utils';

import { LikeMutation, FollowMutation } from 'apps/content/gql';

import i18next from 'i18next';

const { imageDefaults } = globalConf;

export class ContentModel extends Model {
  static registryName = 'ContentModel';
  static ModelProps = {
    // inSpace: 'SpaceModel',
    avatar: 'FileModel',
    creator: 'UserModel',
    parentContent: 'ContentModel',
    content: 'ContentModel',
    inSpace: 'SpaceModel',
  };

  @observable canUpdate = null;
  @observable canDelete = null;
  @observable content = null;
  @observable inSpace = null;
  @observable avatar = null;
  @observable slug = '';
  @observable title = '';
  @observable imageFallbackChar = '';
  @observable type = '';
  @observable isLiked;
  @observable likesCount;
  @observable isFollowed;
  @observable followersCount = 0;
  @observable creator;
  @observable parentContent;
  @observable version;
  @observable origin;
  @observable whitelabel;
  @observable description;
  @observable isPrivate;
  @observable parentSlug;
  @observable pageViews;

  @computed get imageFallbackPath() {
    if (this.imageFallbackChar) {
      return `/assets/avatars/${this.imageFallbackChar}.jpeg`;
    }
    // only works for profile/initiative
    return imageDefaults[this.type.toLowerCase()];
  }

  @computed get contentType() {
    return this.type.charAt(0).toUpperCase() + this.type.slice(1);
  }

  @computed get pageViewsFormatted() {
    const views = this.pageViews;
    return Math.abs(views) > 999
      ? `${Math.sign(views) * (Math.abs(views) / 1000).toFixed(1)}k`
      : Math.sign(views) * Math.abs(views);
  }
}
ModelRegistry.add(ContentModel);

export class ContentApp extends App {
  static registryName = 'ContentApp';
  static Model = ContentModel;
  static AppProps = {
    // inSpace: 'SpaceApp',
    avatar: 'FileApp',
    creator: 'UserApp',
    parentContent: 'ContentApp',
    content: 'ContentApp',
    inSpace: 'SpaceApp',
  };

  ownUnlikableContentTypes = ['Comment'];

  init() {
    this.SetLike = new LikeMutation(
      new Input({
        contentId: () => this.id,
        like: () => this.isLiked,
      }),
    );
    // this.Like.process((content) => ContentModel.Load(content));
    this.SetFollow = new FollowMutation(
      new Input({
        contentId: () => this.id,
        follow: () => this.isFollowed,
      }),
    );
  }

  @action Like = () => {
    if (clientStore.signedInProfile === null) {
      modalStore.SetModalComponent(SignInModal);
    } else {
      this.isLiked = true;
      this.likesCount += 1;
      this.SetLike.mutate({
        onSysErr: this.onLikeErr,
        onSuccess: this.onLikeSuccess,
      });
    }
  };

  @action UnLike = () => {
    const { contentType } = this;
    if (
      clientStore.signedInProfile &&
      clientStore.signedInProfile.username === this.creator.username &&
      !this.ownUnlikableContentTypes.includes(contentType)
    ) {
      Alert.success(
        i18next.t("☝️You can't un-like your own {{contentType}}", {
          contentType,
        }),
      );
    } else {
      this.isLiked = false;
      this.likesCount -= 1;
      this.SetLike.mutate({
        onSysErr: this.onUnLikeErr,
        onSuccess: this.onLikeSuccess,
      });
    }
  };

  @action onLikeErr = error => {
    this.ResetLocals(['isLiked', 'likesCount']);
    raiseUserError(error, 'like', this.type);
  };

  @action onLikeSuccess = () => {
    this.ResetLocals(['isLiked', 'likesCount']);
  };

  @action onUnLikeErr = error => {
    this.ResetLocals(['isLiked', 'likesCount']);
    raiseUserError(error, 'un-like', this.type);
  };

  @action FollowUser = () => {
    if (clientStore.signedInProfile === null) {
      modalStore.SetModalComponent(SignInModal);
    } else {
      analytics.sendEvent('clicked-following', {}, { ga: true });
      this.isFollowed = true;
      this.followersCount += 1;
      this.SetFollow.mutate({
        onSysErr: this.onFollowSysError,
        onUserErr: this.onFollowUserError,
        onSuccess: this.onFollowSuccess,
      });
    }
  };

  @action UnFollow = () => {
    if (
      clientStore.signedInProfile &&
      clientStore.signedInProfile.username === this.creator.username
    ) {
      const { contentType } = this;
      Alert.success(
        i18next.t("☝️You can't un-follow your own {{contentType}}", {
          contentType,
        }),
      );
    } else {
      this.isFollowed = false;
      this.followersCount -= 1;
      this.SetFollow.mutate({
        onSysErr: this.onUnFollowSysError,
        onUserErr: this.onUnFollowUserError,
        onSuccess: this.onFollowSuccess,
      });
    }
  };

  @action onUnFollowSysError = () => {
    this.ResetLocals(['isFollowed', 'followersCount']);
    Alert.error(
      i18next.t(
        'Hmm.. Something went very wrong. Please try again. Contact us if the problem remains.',
      ),
    );
  };

  @action onUnFollowUserError = error => {
    this.ResetLocals(['isFollowed', 'followersCount']);
    raiseUserError(error, 'un-follow', this.type);
  };

  @action onFollowSysError = () => {
    this.ResetLocals(['isFollowed', 'followersCount']);
    Alert.error(
      i18next.t(
        'Hmm.. Something went very wrong. Please try again. Contact us if the problem remains.',
      ),
    );
  };

  @action onFollowUserError = error => {
    this.ResetLocals(['isFollowed', 'followersCount']);
    raiseUserError(error, 'follow', this.type);
  };

  @action onFollowSuccess = () => {
    analytics.sendEvent(
      'Follows',
      { content_name: this.slug, value: 6, currency: 'EUR' },
      { facebook: true },
    );
    this.ResetLocals(['isFollowed', 'followersCount']);
  };
}
AppRegistry.add(ContentApp);
